// Écran 6 — Exports dans les standards ouverts (OCSF, CycloneDX, OSCAL, STIX, six vues de l'ANSSI, ArchiMate réservé
// au développement) et journal des écritures (une ligne par écriture acceptée, avec sa différence exacte).
import { useState } from 'react';
import type { Acces, EntreeJournal } from '../api/types';
import { Attendre, Difference, Erreur, Etiquette } from '../composants/communs';
import { useCarte, useEkera, useEvenements, useJournal, useJournalAcces } from '../contexte';
import { dateFr, LIBELLE_OPERATION, VUES_ANSSI } from '../domaine/format';
import { telecharger } from '../domaine/telechargement';

interface Export {
  chemin: string;
  fichier: string;
  titre: string;
  norme: string;
  contenu: string;
}

const EXPORTS: Export[] = [
  { chemin: 'ocsf', fichier: 'constats-ocsf.json', titre: 'Constats', norme: 'OCSF 1.9.0', contenu: 'Vulnerability Finding et Compliance Finding, pertes potentielles estimées (en euros) et provenance' },
  { chemin: 'cyclonedx', fichier: 'inventaire-cyclonedx.json', titre: 'Inventaire', norme: 'CycloneDX 1.6', contenu: 'Applications, machines, sauvegardes et leurs dépendances' },
  { chemin: 'oscal', fichier: 'evaluation-oscal.json', titre: 'Résultats d’évaluation', norme: 'OSCAL 1.1.3', contenu: 'Une constatation par constat, avec les citations en preuve' },
];

export function EcranExports() {
  const carte = useCarte();
  const signaux = (carte.data?.objets ?? []).filter((o) => o.type === 'signal');
  return (
    <div className="pile">
      <p className="sous-titre">
        Votre carte vous appartient : elle sort dans des formats ouverts, lisibles par vos autres outils (GRC, CMDB, SIEM),
        tels que l’API les produit. Chaque écriture faite sur la carte est journalisée.
      </p>
      <section className="panneau" aria-labelledby="t-exports">
        <h2 id="t-exports">Exports</h2>
        <div className="grille g3">
          {EXPORTS.map((e) => (
            <CarteExport key={e.chemin} e={e} />
          ))}
          <div className="option">
            <h3>Signaux</h3>
            <p className="note">STIX 2.1 · un paquet par signal (produit, version, faille, confiance)</p>
            {signaux.length === 0 ? (
              <p className="muted">Aucun signal dans cette carte.</p>
            ) : (
              signaux.map((s) => (
                <CarteExport
                  key={s.id}
                  compact
                  e={{ chemin: `stix/${encodeURIComponent(s.id)}`, fichier: `signal-${s.id.replace(/\W+/g, '-')}.json`, titre: s.nom, norme: 'STIX 2.1', contenu: '' }}
                />
              ))
            )}
          </div>
          <CarteExport
            e={{
              chemin: 'archimate',
              fichier: 'carte-archimate.xml',
              titre: 'Architecture',
              norme: 'ArchiMate 3.1, format d’échange',
              contenu: 'Réservé au développement tant que la licence commerciale de The Open Group n’est pas obtenue',
            }}
          />
        </div>
      </section>
      <section className="panneau" aria-labelledby="t-anssi">
        <h2 id="t-anssi">Les six vues du guide de cartographie de l’ANSSI</h2>
        <p className="note">CSV, séparateur « ; », UTF-8. Colonnes choisies par Ekera, à relire avec le guide (2018).</p>
        <div className="grille g3">
          {VUES_ANSSI.map((v) => (
            <CarteExport
              key={v.id}
              compact
              e={{ chemin: `anssi/${v.id}`, fichier: `anssi-${v.id}.csv`, titre: v.libelle, norme: v.description, contenu: '' }}
            />
          ))}
        </div>
      </section>
      <Journal />
      <JournalAcces />
    </div>
  );
}

function CarteExport({ e, compact = false }: { e: Export; compact?: boolean }) {
  const { client, cid } = useEkera();
  const [etat, setEtat] = useState<{ occupe: boolean; erreur: unknown; taille: number | null }>({ occupe: false, erreur: null, taille: null });
  async function lancer() {
    setEtat({ occupe: true, erreur: null, taille: null });
    try {
      const r = await client.exporter(cid, e.chemin);
      telecharger(r.contenu, `${cid}-${e.fichier}`);
      setEtat({ occupe: false, erreur: null, taille: r.contenu.size });
    } catch (err) {
      setEtat({ occupe: false, erreur: err, taille: null });
    }
  }
  return (
    <div className={compact ? 'pile' : 'option'} style={compact ? { gap: 6 } : undefined}>
      <h3>{e.titre}</h3>
      <p className="note">
        {e.norme}
        {e.contenu ? ` · ${e.contenu}` : ''}
      </p>
      <div className="ligne">
        <button type="button" className="btn petit" onClick={() => void lancer()} disabled={etat.occupe} aria-label={`Télécharger ${e.titre} (${e.norme})`}>
          {etat.occupe ? 'Export…' : 'Télécharger'}
        </button>
        {etat.taille !== null && (
          <span className="note" role="status">
            {Math.max(1, Math.round(etat.taille / 1024))} Ko téléchargés
          </span>
        )}
      </div>
      {etat.erreur ? <Erreur erreur={etat.erreur} titre="Export refusé." /> : null}
    </div>
  );
}

function Journal() {
  const journal = useJournal();
  const evenements = useEvenements();
  const [ouverte, setOuverte] = useState<string | null>(null);
  return (
    <section className="panneau" aria-labelledby="t-journal">
      <div className="panneau-tete">
        <h2 id="t-journal">Journal des écritures</h2>
        <span className="indice">
          {evenements.data ? `${evenements.data.length} événement(s) CloudEvents produits (aucun envoi réseau en v0)` : ''}
        </span>
      </div>
      <Attendre etats={[journal]} quoi="du journal">
        {() =>
          journal.data!.length === 0 ? (
            <p className="muted">Aucune écriture pour l’instant. Les corrections validées apparaîtront ici.</p>
          ) : (
            <div className="tableau-defile">
              <table>
                <caption className="sr">Écritures acceptées, de la plus récente à la plus ancienne</caption>
                <thead>
                  <tr>
                    <th scope="col">Écriture</th>
                    <th scope="col">Reçue le</th>
                    <th scope="col">Auteur</th>
                    <th scope="col">Opération</th>
                    <th scope="col">Élément</th>
                    <th scope="col">Versions</th>
                    <th scope="col">Différence</th>
                  </tr>
                </thead>
                <tbody>
                  {[...journal.data!].reverse().map((j) => (
                    <LigneJournal key={j.ecriture} j={j} ouverte={ouverte === j.ecriture} basculer={() => setOuverte(ouverte === j.ecriture ? null : j.ecriture)} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Attendre>
    </section>
  );
}

/** Journal d'accès du client (rôle administration) : chaque requête de chaque personne, identité vérifiée par l'API. */
function JournalAcces() {
  const { administrateur } = useEkera();
  const acces = useJournalAcces(administrateur);
  if (!administrateur) return null;
  return (
    <section className="panneau" aria-labelledby="t-acces" data-testid="journal-acces">
      <div className="panneau-tete">
        <h2 id="t-acces">Journal d’accès</h2>
        <span className="indice">Qui a lu ou écrit quoi, quand (sans contenu) · rôle administration</span>
      </div>
      <Attendre etats={[acces]} quoi="du journal d’accès">
        {() => (
          <div className="tableau-defile">
            <table>
              <caption className="sr">Accès à l’API, du plus récent au plus ancien</caption>
              <thead>
                <tr>
                  <th scope="col">Instant</th>
                  <th scope="col">Personne (sujet)</th>
                  <th scope="col">Requête</th>
                  <th scope="col">Carte</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                {acces.data!.map((a: Acces) => (
                  <tr key={a.numero}>
                    <td>{dateFr(a.instant)}</td>
                    <td className="num">{a.sujet}</td>
                    <td className="num">
                      {a.methode} {a.route}
                    </td>
                    <td className="num">{a.carte ?? '—'}</td>
                    <td>
                      <Etiquette ton={a.statut < 400 ? 'ok' : 'grave'}>{a.statut}</Etiquette>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Attendre>
    </section>
  );
}

function LigneJournal({ j, ouverte, basculer }: { j: EntreeJournal; ouverte: boolean; basculer: () => void }) {
  const corr = j.correction as { motif?: string; format?: string; validations_importees?: number };
  const motif = corr.motif;
  const charge = j.operation === 'charger';
  return (
    <>
      <tr>
        <td>
          <span className="num">{j.ecriture}</span>
        </td>
        <td>{dateFr(j.recu_le)}</td>
        <td>{j.auteur ?? '—'}</td>
        <td>
          <Etiquette ton={j.operation === 'retirer' ? 'attention' : 'info'}>{LIBELLE_OPERATION[j.operation] ?? j.operation}</Etiquette>
          {motif && <small className="muted" style={{ display: 'block' }}>{motif}</small>}
          {charge && (
            <small className="muted" style={{ display: 'block' }} data-testid="journal-charger">
              Carte chargée par l’opérateur
              {corr.format ? ` (format ${corr.format})` : ''}
              {typeof corr.validations_importees === 'number'
                ? ` · ${corr.validations_importees} validation${corr.validations_importees > 1 ? 's' : ''} importée${corr.validations_importees > 1 ? 's' : ''}`
                : ''}
            </small>
          )}
        </td>
        <td style={{ overflowWrap: 'anywhere' }}>
          <span className="num">{j.element}</span>
        </td>
        <td className="num">
          {j.version_avant ?? '—'} → {j.version_apres}
        </td>
        <td>
          <button type="button" className="lien-btn" aria-expanded={ouverte} onClick={basculer}>
            {ouverte ? 'Masquer' : `Voir (${j.difference.length})`}
          </button>
        </td>
      </tr>
      {ouverte && (
        <tr>
          <td colSpan={7}>
            <Difference difference={j.difference} element={j.element} />
            <p className="note">
              Clé d’idempotence : <span className="num">{j.cle_idempotence}</span> · révision de la carte {j.revision}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
